import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { message } from "src/components/Message";
import { DC } from "src/dataCollector/DataCollector";
import { ExportData } from "src/managers/ExportData";
import { buildDownloadFileName } from "src/managers/ServitutDownloadNaming";

export const TINGBOG_SCANNED_AKT_PREFIX =
  "https://www.tinglysning.dk/tinglysning/rest/indskannetakt/rd/";
export const TINGBOG_NEW_AKT_PREFIX =
  "https://www.tinglysning.dk/tinglysning/rest/pdf/atd/";

/** Thrown for API Gateway 504 so callers can show a specific message (not "tingbog down"). */
export const TINGBOG_GATEWAY_TIMEOUT_ERROR = "TINGBOG_GATEWAY_TIMEOUT";

/** Browser often hides 504 behind CORS (no ACAO on error) → TypeError instead of response.status. */
function isOpaqueGatewayFetchFailure(error: unknown): boolean {
  if (error instanceof TypeError && error.name === "AbortError") {
    return false;
  }
  if (!(error instanceof TypeError)) {
    return false;
  }
  const m = String(error.message).toLowerCase();
  return (
    m.includes("failed to fetch") ||
    m.includes("load failed") ||
    m.includes("networkerror when attempting to fetch")
  );
}


function notifyTingbogDownloadFailure(context: string, error: unknown) {
  console.error(context, error);
  const isGatewayTimeout =
    error instanceof Error && error.message === TINGBOG_GATEWAY_TIMEOUT_ERROR;
  message.error(
    isGatewayTimeout
      ? Localization.getText("Tinglysning PDF gateway timeout")
      : getTingbogDownloadErrorMessage(),
  );
}

type TinglysningGatewayResponse = {
  body?: string;
  url?: string;
};

export type TingbogDocumentRef = Partial<{
  dokumentId: string;
  dokumentFilnavn: string;
  aktAliasId: string;
  aktId: string;
  revisionNummer: string;
}>;

export type TingbogDownloadTarget =
  | { kind: "akt"; value: string }
  | {
    kind: "gatewayDokumentPdf";
    dokumentIdentifikator: string;
    revisionDokumentIdentifikator?: string;
  };

/** Digital tingbog document: PDF via gateway (getDokument), not scanned akt file id. */
export function getTingbogGatewayPdfDokumentId(
  document?: TingbogDocumentRef,
): string | undefined {
  if (!isTingbogElectronicDocument(document)) {
    return undefined;
  }
  const dokumentId = document?.dokumentId?.trim();
  if (!dokumentId) {
    return undefined;
  }
  return dokumentId;
}

export type EjendomSummariskPdfRequest = {
  format: "pdf";
  cadastralDistrictIdentifier: string;
  matrikelnummer: string;
};

/**
 * Kun tingbog attest (`getEjendomSummarisk`): matrikelnummerets taldel skal være 4 cifre (fx 0184b).
 * Servitut-PDF (`getDokument`) bruger `dokumentIdentifikator` som UUID uden denne normalisering.
 */
export function normalizeMatrikelnummerForEjendomSummarisk(
  matrikelnummer: string,
): string {
  const trimmed = matrikelnummer.trim();
  const match = /^(\d+)([a-zA-Z]*)$/i.exec(trimmed);
  if (!match) {
    return trimmed;
  }
  const digits = match[1];
  const suffix = match[2] ?? "";
  return `${digits.padStart(4, "0")}${suffix}`;
}

const TINGLYSNING_PDF_MIN_INSTANT_MS = Date.UTC(2009, 0, 1, 0, 0, 0, 0);

export function parseTinglysningsDatoToUtcMs(raw: string | undefined): number | null {
  if (!raw?.trim()) {
    return null;
  }
  const s = raw.trim();
  const fromIso = Date.parse(s);
  if (!Number.isNaN(fromIso)) {
    return fromIso;
  }
  const dm = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(s);
  if (!dm) {
    return null;
  }
  const day = Number(dm[1]);
  const month = Number(dm[2]) - 1;
  const year = Number(dm[3]);
  const t = Date.UTC(year, month, day);
  return Number.isNaN(t) ? null : t;
}

export function isTingbogPdfEligibleByDate(
  tinglysningsDato: string | undefined,
): boolean {
  const ms = parseTinglysningsDatoToUtcMs(tinglysningsDato);
  if (ms === null) {
    return false;
  }
  return ms >= TINGLYSNING_PDF_MIN_INSTANT_MS;
}

export function getTingbogDownloadErrorMessage(): string {
  return Localization.getText("Issues with tingbogen, try again later");
}

export function getTingbogPdfUrl(
  document?: TingbogDocumentRef
): string | undefined {
  const filnavn = document?.dokumentFilnavn?.trim();
  if (filnavn) {
    return TINGBOG_SCANNED_AKT_PREFIX + filnavn;
  }
  const dokumentId = document?.dokumentId?.trim();
  const aktAliasId = document?.aktAliasId?.trim();
  const aktId = document?.aktId?.trim();
  if (aktAliasId && dokumentId) {
    return TINGBOG_NEW_AKT_PREFIX + dokumentId;
  }
  if (!aktId && dokumentId) {
    return TINGBOG_NEW_AKT_PREFIX + dokumentId;
  }
  return undefined;
}

export function isTingbogElectronicDocument(
  document?: TingbogDocumentRef
): boolean {
  if (!document) return false;
  if (document.dokumentFilnavn?.trim()) return false;
  if (document.aktAliasId?.trim()) return true;
  const dokumentId = document.dokumentId?.trim();
  const aktId = document.aktId?.trim();
  return Boolean(dokumentId && !aktId);
}

export function shouldRenderTingbogDownloadButton(
  document?: TingbogDocumentRef
): boolean {
  return getTingbogDownloadTarget(document) !== undefined;
}

export function getTingbogDownloadTarget(
  document?: TingbogDocumentRef
): TingbogDownloadTarget | undefined {
  const filnavn = document?.dokumentFilnavn?.trim();
  if (filnavn) {
    return { kind: "akt", value: filnavn };
  }

  const gatewayId = getTingbogGatewayPdfDokumentId(document);
  if (gatewayId) {
    return { kind: "gatewayDokumentPdf", dokumentIdentifikator: gatewayId };
  }

  const aktId = document?.aktId?.trim();
  if (aktId) {
    return { kind: "akt", value: aktId };
  }

  return undefined;
}

export function getTingbogDownloadFileName(
  label?: string,
  prefix = "tingbog"
): string {
  return buildDownloadFileName(label, {
    defaultStem: prefix,
    defaultExtension: "pdf",
    preserveSpaces: false,
  });
}

function extractPdfFromSoapXml(xmlText: string): Blob {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "application/xml");
  const ns = "http://rep.oio.dk/tinglysning.dk/schema/elektroniskakt/1/";
  const encodedPdf =
    doc.getElementsByTagNameNS(ns, "IndskannetDokumentData")[0]?.textContent?.trim() ||
    doc.getElementsByTagName("IndskannetDokumentData")[0]?.textContent?.trim() ||
    "";

  if (!encodedPdf) {
    throw new Error("PDF data not found in SOAP response");
  }

  const binary = atob(encodedPdf);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: "application/pdf" });
}

function base64ToPdfBlob(base64: string): Blob {
  // Gateways may wrap base64 across lines inside JSON; atob rejects whitespace.
  const compact = base64.replace(/\s/g, "");
  const binary = atob(compact);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: "application/pdf" });
}

/**
 * Tinglysning execute-api calls (base URL from `tinglysningApiGatewayHost` in environment settings).
 * getDokument PDF is equivalent to:
 * curl -sS -X POST "{host}/getDokument" -H "x-api-key: …" -H "Content-Type: application/json"
 *   -d '{"dokumentIdentifikator":"<uuid>","format":"pdf"}'
 */
async function postTinglysningGatewayJson(
  path: string,
  body: Record<string, unknown>,
): Promise<{ body?: string }> {
  const pdfServiceUrl =
    SettingsManager.getSystemSetting("tinglysningApiGatewayHost")?.trim() ?? "";
  const pdfServiceApiKey =
    SettingsManager.getSystemSetting("tinglysningApiGatewayKey")?.trim() ?? "";

  if (!pdfServiceUrl || !pdfServiceApiKey) {
    throw new Error("Missing tinglysning API configuration");
  }

  const apiUrl = `${pdfServiceUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
  let response: Response;
  try {
    response = await fetch(apiUrl, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": pdfServiceApiKey,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    if (isOpaqueGatewayFetchFailure(e)) {
      throw new Error(TINGBOG_GATEWAY_TIMEOUT_ERROR);
    }
    throw e;
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    if (response.status === 504) {
      throw new Error(TINGBOG_GATEWAY_TIMEOUT_ERROR);
    }
    if (response.status === 503 || /TLWebXML.*503|503.*TLWebXML/i.test(errorText)) {
      throw new Error("Tinglysning service is temporarily unavailable. Please try again later.");
    }
    throw new Error(`Tinglysning API error: ${response.status} - ${errorText.substring(0, 200)}`);
  }

  const parsed = await response.json().catch(() => null);
  const envelope = (parsed ?? {}) as { statusCode?: number; body?: string };

  const bodyStr = typeof envelope.body === "string" ? envelope.body : undefined;
  console.log("[tinglysning gateway]", path, {
    ...envelope,
    body: bodyStr
      ? bodyStr.length > 600
        ? `[base64 length=${bodyStr.length}, preview=${bodyStr.slice(0, 160)}…]`
        : bodyStr
      : envelope.body,
  });

  // Lambda proxy sometimes returns HTTP 200 with an error in the JSON envelope.
  if (
    typeof envelope.statusCode === "number" &&
    envelope.statusCode >= 400
  ) {
    throw new Error(
      (typeof envelope.body === "string" && envelope.body.trim().slice(0, 400)) ||
      `Tinglysning gateway statusCode ${envelope.statusCode}`,
    );
  }
  return envelope;
}

export async function fetchTingbogDokumentPdfBlob(
  dokumentIdentifikator: string,
  revisionDokumentIdentifikator?: string,
): Promise<Blob> {
  const id = dokumentIdentifikator.trim();
  if (!id) {
    throw new Error("Missing dokumentIdentifikator");
  }
  const payload: Record<string, unknown> = {
    dokumentIdentifikator: id,
    format: "pdf",
  };
  if (revisionDokumentIdentifikator?.trim()) {
    payload._dokumentIdentifikator = revisionDokumentIdentifikator.trim();
  }

  const raw = await postTinglysningGatewayJson("getDokument", payload);
  const b64 = raw.body?.trim();
  if (!b64) {
    throw new Error("PDF data not found in API response");
  }
  return base64ToPdfBlob(b64);
}

export async function fetchEjendomSummariskPdfBlob(
  params: EjendomSummariskPdfRequest,
): Promise<Blob> {
  const raw = await postTinglysningGatewayJson("getEjendomSummarisk", {
    cadastralDistrictIdentifier: params.cadastralDistrictIdentifier,
    matrikelnummer: normalizeMatrikelnummerForEjendomSummarisk(params.matrikelnummer),
    format: "pdf",
  });
  const b64 = raw.body?.trim();
  if (!b64) {
    throw new Error("PDF data not found in API response");
  }
  return base64ToPdfBlob(b64);
}

async function resolveTingbogAktFileUrl(aktId: string): Promise<string> {
  const pdfServiceUrl =
    SettingsManager.getSystemSetting("tinglysningApiGatewayHost")?.trim() ?? "";
  const pdfServiceApiKey =
    SettingsManager.getSystemSetting("tinglysningApiGatewayKey")?.trim() ?? "";

  if (!pdfServiceUrl || !pdfServiceApiKey) {
    throw new Error("Missing tinglysning API configuration");
  }

  const apiUrl = `${pdfServiceUrl.replace(/\/+$/, "")}/getTinglysningsAkt`;
  let response: Response;
  try {
    response = await fetch(apiUrl, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": pdfServiceApiKey,
      },
      body: JSON.stringify({
        AktDokumentId: aktId,
        storeToFile: "true",
      }),
    });
  } catch (e) {
    if (isOpaqueGatewayFetchFailure(e)) {
      throw new Error(TINGBOG_GATEWAY_TIMEOUT_ERROR);
    }
    throw e;
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    if (response.status === 503 || /TLWebXML.*503|503.*TLWebXML/i.test(errorText)) {
      throw new Error("Tinglysning service is temporarily unavailable. Please try again later.");
    }
    throw new Error(`Tinglysning API error: ${response.status} - ${errorText.substring(0, 200)}`);
  }

  const raw = (await response.json().catch(() => null)) as TinglysningGatewayResponse | null;
  if (!raw) {
    throw new Error("Invalid API response");
  }

  let innerBody: { url?: string } | null = null;
  if (typeof raw.body === "string") {
    try {
      innerBody = JSON.parse(raw.body) as { url?: string };
    } catch {
      innerBody = null;
    }
  }

  const fileUrl = raw.url ?? innerBody?.url;
  if (!fileUrl || typeof fileUrl !== "string") {
    throw new Error("API did not return file URL");
  }

  return fileUrl;
}

export async function fetchTingbogPdfBlob(aktId: string): Promise<Blob> {
  const fileUrl = await resolveTingbogAktFileUrl(aktId);
  const response = await fetch(fileUrl);

  if (!response.ok) {
    throw new Error(`Failed to fetch file from storage (${response.status})`);
  }

  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  const blob = await response.blob();

  if (contentType.includes("xml")) {
    return extractPdfFromSoapXml(await blob.text());
  }

  const blobText = await blob.text();
  if (blobText.trimStart().startsWith("<")) {
    return extractPdfFromSoapXml(blobText);
  }

  return blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
}

export async function fetchTingbogDocumentBlob(
  target: TingbogDownloadTarget,
): Promise<Blob> {
  return target.kind === "gatewayDokumentPdf"
    ? fetchTingbogDokumentPdfBlob(
      target.dokumentIdentifikator,
      target.revisionDokumentIdentifikator,
    )
    : fetchTingbogPdfBlob(target.value);
}

export async function downloadTingbogDocument(
  target: TingbogDownloadTarget | undefined,
  fileName?: string
): Promise<void> {
  if (!target) {
    message.error(getTingbogDownloadErrorMessage());
    return;
  }

  try {
    const blob = await fetchTingbogDocumentBlob(target);
    ExportData.downloadBlob(
      buildDownloadFileName(fileName, {
        defaultStem: "tingbog",
        defaultExtension: "pdf",
        preserveSpaces: true,
      }),
      blob,
    );
  } catch (error) {
    notifyTingbogDownloadFailure("Failed to download Tingbog document", error);
  }
}

export async function downloadEjendomSummariskPdf(
  params: Parameters<typeof DC.TL.EjendomSummariskPDF>[0],
  fileName?: string,
): Promise<void> {
  try {
    const blob = await DC.TL.EjendomSummariskPDF(params);
    ExportData.downloadBlob(
      buildDownloadFileName(fileName ?? "tingbog_attest", {
        defaultStem: "tingbog_attest",
        defaultExtension: "pdf",
        preserveSpaces: true,
      }),
      blob,
    );
  } catch (error) {
    notifyTingbogDownloadFailure("Failed to download ejendom summarisk PDF", error);
  }
}
